/**
 * IMPORTANT:
 * This is not an attempt at "legit" IP vs domain matching,
 * but an attempt to match apparent logic on device
 */
import {
  boolean as yupBoolean,
  mixed,
  number as yupNumber,
  Schema,
  string as yupString,
  object as yupObject,
  ObjectSchemaDefinition
} from "yup";

export const isValidIpOrDomain = (value: string | null | undefined) => {
  //As this is a schema helper, we don not check if there is no value, that is .notRequired or .required's job.
  if (!value) {
    return true;
  }
  value = value.trim();
  if (!value) {
    return true;
  }
  // Cannot end or start with a dot or dash (or end with colon)
  if (/^[.-]/.test(value) || /[:.-]$/.test(value)) {
    return false;
  }

  // Are there colons? If so, treat as IPv6 address (properly) and return
  // NOTE: Device does NOT appear to support ports in these fields, so colon should be safe?
  if (value.indexOf("::") > -1) {
    return /(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))/.test(
      value
    );
  }

  // Port number at end? Intentionally loose here...
  if (/:\d+$/.test(value)) {
    const port = /:(\d+)$/.exec(value);

    if (!port || +port[1] > 65535 || /^0/.test(port[1])) {
      return false;
    }

    value = value.replace(/:\d{1,5}$/, "");
  }

  const labels = value.split(".").map(l => l.trim());

  // all of the labels must be 63 characters or less
  if (labels.some(l => l.length > 63)) {
    return false;
  }

  // Is the last label purely numeric? (Note: first thought it was ANY label, but device ignores numeric labels at start / middle)
  // If so, treat as IP v4 address (properly) and return
  if (!isNaN(labels[labels.length - 1] as any)) {
    return /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
      value
    );
  }

  // each of the labels cannot start or end with a dot or dash
  if (labels.some(l => !l || /^[.-]/.test(l) || /[.-]$/.test(l))) {
    return false;
  }

  // no apparent limit on the NUMBER of labels.  One or 125 seem accepted.
  return true;
};

/**
 * Attached to an object field to disable schema checks when an associated 'enable'
 * flag is falsy.  For example, to diable an auth schema object based on an enable falg at the
 * same level
 *
 * enable: boolean("ENABLE_CALENDARING"),
 * auth: auth.when("enable", whenDisabled(object())),
 * @param whenDisabled
 */
export function whenDisabled(whenDisabled: Schema<any>) {
  return (enable: boolean | undefined, root: Schema<any>) => {
    if (enable === false) {
      return whenDisabled;
    }
    return root;
  };
}

/**
 * Setup a bunch of defaults on the yup schema's.
 * Typically everything on the SettingsResponse is both nullable and not required.
 * @param values
 * @param label
 */
export function mixedEnum(values: any, label: string) {
  return mixed()
    .label(label)
    .nullable()
    .oneOf([...Object.values(values), null])
    .notRequired();
}

export function stringF(label: string) {
  return yupString()
    .label(label)
    .nullable()
    .notRequired()
    .default("");
}

export function object<T extends object = {}>(
  fields?: ObjectSchemaDefinition<T>
) {
  return yupObject<T>(fields).nullable();
}

export function number(label: string) {
  return yupNumber()
    .label(label)
    .default(0)
    .notRequired()
    .nullable();
}

export function boolean(label: string) {
  return yupBoolean()
    .label(label)
    .nullable()
    .default(false);
}
